import { useEffect } from "react";
import LoadCSSforfirstTime from "../../../CloudStorage/LoadCSSforfirstTime";

import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { getPaymentType, payPayment } from "../../../redux/common/action";
import { useLocation, useNavigate } from "react-router-dom";
import { urtoast } from "../../Layouts/CustomPopup/eventEmitter";
import AsteriskLabel from "../../Common/AsteriskLabel";
import { useState } from "react";
import CloudStorage from "../../../CloudStorage/CloudStorage";

/* ---------- HELPERS ---------- */

const luhnCheck = (num) => {
  let sum = 0;
  let alternate = false;

  for (let i = num.length - 1; i >= 0; i--) {
    let n = parseInt(num[i], 10);
    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
};

const cardRules = {
  VISA: { lengths: [13, 16], cvv: 3 },
  MASTER: { lengths: [16], cvv: 3 },
  MASTERCARD: { lengths: [16], cvv: 3 },
  AMEX: { lengths: [15], cvv: 4 },
  MAESTRO: { lengths: [12, 13, 14, 15, 16, 17, 18, 19], cvv: 3 },
  VISAELECTRON: { lengths: [16], cvv: 3 },
  VPAY: { lengths: [16], cvv: 3 },
};

export default function SubmitWithdrawAmountForm() {
  const path = "Assets/scss/FindWork.module.scss";

  const [withdrawType, setWithdrawType] = useState(null); // 'card' or 'bank'

  useEffect(() => {
    LoadCSSforfirstTime(path);
  }, [path]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { state = {} } = location;

  const walletAmount = Number(state?.wallet || 0);

  const paymenttypelist = useSelector(
    ({ common: { paymenttypelist } }) => paymenttypelist
  );
  const profile = useSelector(({ profile: { profiledata } }) => profiledata);

  useEffect(() => {
    dispatch(getPaymentType());
  }, [dispatch]);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const formik = useFormik({
    initialValues: {
      type: "15",
      price: "",
      payment_brand: "",
      card_number: "",
      card_holder: "",
      card_expiry_month: "",
      card_expiry_year: "",
      card_cvv: "",
      bank_name: profile?.bank_details?.bank_name || "",
      account_holder_name: profile?.bank_details?.account_holder_name || "",
      account_number: profile?.bank_details?.account_number || "",
      ifsc_code: profile?.bank_details?.ifsc_code || "",
      swift_code: profile?.bank_details?.swift_code || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      price: Yup.number()
        .required("Amount is required")
        .moreThan(0, "Amount must be greater than 0")
        .max(walletAmount, `Cannot exceed wallet balance (${walletAmount})`)
        .typeError("Invalid amount"),

      // CARD VALIDATIONS
      payment_brand: Yup.string().when("type", {
        is: "15",
        then: (schema) => schema.required("Select payment brand"),
        otherwise: (schema) => schema.notRequired(),
      }),
      card_number: Yup.string().when("type", {
        is: "15",
        then: (schema) =>
          schema
            .required("Card number is required")
            .matches(/^\d+$/, "Digits only")
            .test("length", "Invalid card number", function (value) {
              const rule = cardRules[this.parent.payment_brand];
              return rule && rule.lengths.includes(value?.length);
            })
            .test("luhn", "Invalid card number", (value) =>
              value ? luhnCheck(value) : false
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
      card_holder: Yup.string().when("type", {
        is: "15",
        then: (schema) =>
          schema
            .required("Card holder name required")
            .matches(/^[A-Za-z ]+$/, "Only letters allowed"),
        otherwise: (schema) => schema.notRequired(),
      }),
      card_expiry_month: Yup.number().when("type", {
        is: "15",
        then: (schema) =>
          schema
            .required("Month required")
            .min(1, "Invalid month")
            .max(12, "Invalid month")
            .typeError("Invalid month"),
        otherwise: (schema) => schema.notRequired(),
      }),
      card_expiry_year: Yup.number().when("type", {
        is: "15",
        then: (schema) =>
          schema
            .required("Year required")
            .min(currentYear, "Card expired")
            .typeError("Invalid year"),
        otherwise: (schema) => schema.notRequired(),
      }),
      card_cvv: Yup.string().when("type", {
        is: "15",
        then: (schema) =>
          schema
            .required("CVV required")
            .matches(/^\d+$/, "Digits only")
            .test("cvv", "Invalid CVV", function (value) {
              const rule = cardRules[this.parent.payment_brand];
              return rule ? value?.length === rule.cvv : false;
            }),
        otherwise: (schema) => schema.notRequired(),
      }),

      // BANK VALIDATIONS
      bank_name: Yup.string().when("type", {
        is: "16",
        then: (schema) => schema.required("Bank name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      account_holder_name: Yup.string().when("type", {
        is: "16",
        then: (schema) => schema.required("Account holder name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      account_number: Yup.string().when("type", {
        is: "16",
        then: (schema) => schema.required("Account number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      ifsc_code: Yup.string().when("type", {
        is: "16",
        then: (schema) => schema.required("IFSC code is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }).test("expiry-check", "Card expiry date is invalid", function (values) {
      if (values.type !== "15") return true;
      const { card_expiry_month, card_expiry_year } = values;
      if (!card_expiry_month || !card_expiry_year) return false;
      if (card_expiry_year === currentYear) {
        return card_expiry_month >= currentMonth;
      }
      return true;
    }),

    onSubmit: (values) => {
      hitApi(values);
    },
  });

  const hitApi = (data) => {
    try {
      dispatch(payPayment(data)).then((res) => {
        if (res?.data?.status) {
          urtoast(res?.data?.response?.message, 1);
          navigate(-1);
        } else {
          urtoast(res?.data?.response?.message, 2);
        }
      });
    } catch (err) {
      urtoast(err?.message || err || "Something Wrong", 1);
    }
  };

  const showError = (name) => formik.touched[name] && formik.errors[name];

  const handleWithdrawTypeSelect = (type) => {
    setWithdrawType(type);
    formik.setFieldValue("type", type === "card" ? "15" : "16");
  };

  if (!withdrawType) {
    return (
      <div className="project-section submit-proposal col-lg-10 mx-auto">
        <div className="col-lg-10 mx-auto mob-pad">
          <div className="proposal-section text-center p-5">
            <h5 className="mb-4">Select Withdrawal Method</h5>
            <div className="row">
              <div className="col-md-6 mb-3">
                <div
                  className="card p-4 cursor-pointer hover-effect"
                  onClick={() => handleWithdrawTypeSelect('card')}
                  style={{ border: '2px solid #ee7811', borderRadius: '15px' }}
                >
                  <h6>Using Card Details</h6>
                  <p className="text-muted small">Instant Withdrawal</p>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div
                  className="card p-4 cursor-pointer hover-effect"
                  onClick={() => handleWithdrawTypeSelect('bank')}
                  style={{ border: '2px solid #ee7811', borderRadius: '15px' }}
                >
                  <h6>Using Bank Details</h6>
                  <p className="text-muted small">On Admin Approval</p>
                </div>
              </div>
            </div>
            <div className="mt-4"  >
              <button className="btn" style={{ borderRadius: '15px', border: '2px solid gray', width: '30%', justifyContent: 'center', alignItems: 'center' }} type="button" onClick={() => navigate(-1)} >Back</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-section submit-proposal col-lg-10 mx-auto">
      <div className="col-lg-10 mx-auto mob-pad">
        <div className="proposal-section">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Submit withdrawal request ({withdrawType === 'card' ? 'Card' : 'Bank'})</h5>
            <button className="btn btn-sm btn-outline-primary" onClick={() => setWithdrawType(null)}>Change Method</button>
          </div>

          <form onSubmit={formik.handleSubmit} noValidate>
            {/* PRICE */}
            <label htmlFor="price">Amount <AsteriskLabel /></label>
            <input
              type="number"
              id="price"
              name="price"
              placeholder={`Max ${walletAmount}`}
              className={`form-control ${showError("price") ? "is-invalid" : ""}`}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.price}
            />
            {showError("price") && (
              <div className="invalid-feedback">{formik.errors.price}</div>
            )}

            {withdrawType === "card" ? (
              <>
                {/* PAYMENT BRAND */}
                <label htmlFor="payment_brand" className="mt-3">Payment Brand <AsteriskLabel /></label>
                <select
                  id="payment_brand"
                  name="payment_brand"
                  className={`form-control ${showError("payment_brand") ? "is-invalid" : ""}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.payment_brand}
                >
                  <option value="">Select card type</option>
                  {paymenttypelist?.types?.map((item, i) => (
                    <option key={i} value={item.value}>
                      {item.value}
                    </option>
                  ))}
                </select>
                {showError("payment_brand") && (
                  <div className="invalid-feedback">{formik.errors.payment_brand}</div>
                )}

                {/* CARD NUMBER */}
                <label htmlFor="card_number" className="mt-3">Card Number <AsteriskLabel /></label>
                <input
                  id="card_number"
                  name="card_number"
                  placeholder="Card number"
                  className={`form-control ${showError("card_number") ? "is-invalid" : ""}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.card_number}
                />
                {showError("card_number") && (
                  <div className="invalid-feedback">{formik.errors.card_number}</div>
                )}

                {/* CARD HOLDER */}
                <label htmlFor="card_holder" className="mt-3">Card Holder Name <AsteriskLabel /></label>
                <input
                  id="card_holder"
                  name="card_holder"
                  placeholder="Card holder name"
                  className={`form-control ${showError("card_holder") ? "is-invalid" : ""}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.card_holder}
                />
                {showError("card_holder") && (
                  <div className="invalid-feedback">{formik.errors.card_holder}</div>
                )}

                {/* EXPIRY + CVV */}
                <div className="row mt-3">
                  <div className="col-md-4">
                    <label htmlFor="card_expiry_month">Expiry Month (MM) <AsteriskLabel /></label>
                    <input
                      id="card_expiry_month"
                      name="card_expiry_month"
                      placeholder="MM"
                      className={`form-control ${showError("card_expiry_month") ? "is-invalid" : ""}`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.card_expiry_month}
                    />
                    {showError("card_expiry_month") && (
                      <div className="invalid-feedback">{formik.errors.card_expiry_month}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="card_expiry_year">Expiry Year (YYYY) <AsteriskLabel /></label>
                    <input
                      id="card_expiry_year"
                      name="card_expiry_year"
                      placeholder="YYYY"
                      className={`form-control ${showError("card_expiry_year") ? "is-invalid" : ""}`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.card_expiry_year}
                    />
                    {showError("card_expiry_year") && (
                      <div className="invalid-feedback">{formik.errors.card_expiry_year}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="card_cvv">CVV <AsteriskLabel /></label>
                    <input
                      id="card_cvv"
                      name="card_cvv"
                      placeholder="CVV"
                      className={`form-control ${showError("card_cvv") ? "is-invalid" : ""}`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.card_cvv}
                    />
                    {showError("card_cvv") && (
                      <div className="invalid-feedback">{formik.errors.card_cvv}</div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* BANK NAME */}
                <label htmlFor="bank_name" className="mt-3">Bank Name <AsteriskLabel /></label>
                <input
                  id="bank_name"
                  name="bank_name"
                  placeholder="Bank Name"
                  className={`form-control ${showError("bank_name") ? "is-invalid" : ""}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.bank_name}
                />
                {showError("bank_name") && (
                  <div className="invalid-feedback">{formik.errors.bank_name}</div>
                )}

                {/* ACCOUNT HOLDER */}
                <label htmlFor="account_holder_name" className="mt-3">Account Holder Name <AsteriskLabel /></label>
                <input
                  id="account_holder_name"
                  name="account_holder_name"
                  placeholder="Account Holder Name"
                  className={`form-control ${showError("account_holder_name") ? "is-invalid" : ""}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.account_holder_name}
                />
                {showError("account_holder_name") && (
                  <div className="invalid-feedback">{formik.errors.account_holder_name}</div>
                )}

                {/* ACCOUNT NUMBER */}
                <label htmlFor="account_number" className="mt-3">Account Number <AsteriskLabel /></label>
                <input
                  id="account_number"
                  name="account_number"
                  placeholder="Account Number"
                  className={`form-control ${showError("account_number") ? "is-invalid" : ""}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.account_number}
                />
                {showError("account_number") && (
                  <div className="invalid-feedback">{formik.errors.account_number}</div>
                )}

                {/* IFSC CODE */}
                <label htmlFor="ifsc_code" className="mt-3">IFSC Code <AsteriskLabel /></label>
                <input
                  id="ifsc_code"
                  name="ifsc_code"
                  placeholder="IFSC Code"
                  className={`form-control ${showError("ifsc_code") ? "is-invalid" : ""}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.ifsc_code}
                />
                {showError("ifsc_code") && (
                  <div className="invalid-feedback">{formik.errors.ifsc_code}</div>
                )}

                {/* SWIFT CODE */}
                <label htmlFor="swift_code" className="mt-3">Swift Code</label>
                <input
                  id="swift_code"
                  name="swift_code"
                  placeholder="Swift Code (Optional)"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.swift_code}
                />
              </>
            )}

            <div className="submit-btn mt-3">
              <button type="button" onClick={() => navigate(-1)} className="btn">
                Cancel
              </button>
              <button type="submit" className="btn post-btn">
                Submit Withdrawal
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
